import { usePutRule } from "hooks/rules/usePutRule";
import React, { useContext, useMemo } from "react";
import {
  RuleDialog,
  RuleFormValues
} from "components/rules-management/RuleDialog/RuleDialog";
import { SnackbarQueueContext } from "contexts/SnackbarQueueContext";
import { Rule } from "interfaces/rule";
import { Tag } from "interfaces/tag";

interface EditRuleDialogProps {
  rule: Rule | undefined;
  open: boolean;
  loadingTags: boolean;
  tags: Tag[];
  onClose: () => void;
  onSuccess: (rule: Rule) => void;
}

export const EditRuleDialog = ({
  rule,
  open,
  loadingTags,
  tags,
  onClose = () => {},
  onSuccess = () => {}
}: EditRuleDialogProps): JSX.Element => {
  const { loading, error, putRuleCallback } = usePutRule(rule?.rule_id);
  const { addSnackbarToQueue } = useContext(SnackbarQueueContext);
  const tag = useMemo(() => {
    if (rule) {
      return tags.find(tag => tag.id === rule.tag_id) ?? null;
    }

    return null;
  }, [rule, tags]);

  const handleSubmit = ({
    ruleName,
    tag,
    hysteresisPoints
  }: RuleFormValues): void => {
    if (rule) {
      const tempRule = {
        name: ruleName,
        tag: tag?.id,
        hysteresis_points: parseInt(hysteresisPoints),
        enabled: rule.rule_enabled
      };

      // @ts-ignore
      putRuleCallback(tempRule).then(
        response => onSuccess(response),
        _error =>
          addSnackbarToQueue({
            message: "Failed to update the rule.",
            severity: "error"
          })
      );
    }
  };

  return (
    <RuleDialog
      open={open}
      loading={loading || loadingTags}
      title="Change rule"
      initialValues={{
        ruleName: rule?.rule_name ?? "",
        tag,
        hysteresisPoints: rule ? rule.hysteresis_points.toString() : "0"
      }}
      error={error}
      errorText="An error occurred while trying to update the rule"
      tags={tag ? [tag] : []}
      disabledTag
      confirmText="Update rule"
      onSubmit={handleSubmit}
      onClose={onClose}
      aria-label="rule edit dialog"
    />
  );
};
